import type {
  EgressAdmissionBlacklist,
  EgressAdmissionBlacklistAction,
} from '@nadoutong/contracts';
import type { I18nKey } from '../i18n/index.ts';
import { useT } from '../i18n/index.ts';
import {
  EGRESS_ADMISSION_BLACKLIST_RULES,
  armedBlacklistRules,
  blacklistWithRule,
  type EgressAdmissionBlacklistRuleKey,
} from '../lib/egressAdmissionBlacklist.ts';
import { isOneOf } from '../lib/isOneOf.ts';
import { Checkbox, FormField, Select } from '../ui/index.ts';

/**
 * The `require-cc-black` per-rule blacklist control (router#1609).
 *
 * Lives on its own rather than inside `EgressConfigEditor` because the egress
 * profile is not the only place the rule set is armed. Admission binds to
 * `upstream_key_channel_bindings` rows, and two SKU namespaces can share one
 * profile while sitting on different bindings — arming a rule on the profile
 * would then refuse traffic that is bare-API by design on the other namespace.
 * The operationally correct arming point is the binding's
 * `egress_config_override`, and `KeyChannelEgressSheet` mounts this same
 * control rather than a second copy of it.
 *
 * Contract, shaped by that second host:
 *
 * - It edits an **admission sub-object**, never a document. The binding write
 *   face is `serializeEgressConfig(parseEgressConfig(...))` and accepts only a
 *   complete `EgressConfig`, so embedding this block into a full document is
 *   the host's job — this component never sees, produces or assumes one. See
 *   `composeBindingBlacklistOverride` in `lib/egressAdmissionBlacklist.ts`.
 * - `null` means the `blacklist` key is absent; an object means it is present,
 *   possibly with nothing armed. The distinction is preserved rather than
 *   collapsed because the runtime parity suite requires that absence and `{}`
 *   not be normalized into each other. Ticking the first rule is what turns
 *   `null` into an object; the component never emits `null`, so a host holding
 *   `null` keeps it until an operator arms something.
 * - **Rule absence is the off state.** Unticking deletes the key rather than
 *   writing a disabled marker, and that lives here rather than in a host.
 */

const EGRESS_ADMISSION_BLACKLIST_ACTIONS = [
  'synthesize-200',
  'model-unavailable',
  'reject',
] as const satisfies readonly EgressAdmissionBlacklistAction[];

/**
 * The action a freshly ticked rule carries. `reject` deliberately: it is the
 * only one of the three that neither fabricates a success nor asserts a
 * model-availability fact, so an operator who arms a rule and never opens the
 * action selector gets an honest refusal rather than a synthesized 200.
 */
const DEFAULT_BLACKLIST_ACTION: EgressAdmissionBlacklistAction = 'reject';

const RULE_LABEL_KEYS: Readonly<Record<EgressAdmissionBlacklistRuleKey, I18nKey>> = {
  firstPartyHealthProbe: 'egress_editor.blacklist_rule.first_party_health_probe',
  bareApiOnCcLane: 'egress_editor.blacklist_rule.bare_api_on_cc_lane',
  agentSdkOnCcLane: 'egress_editor.blacklist_rule.agent_sdk_on_cc_lane',
  missingCcBetaHeader: 'egress_editor.blacklist_rule.missing_cc_beta_header',
};

/**
 * Where the rules being edited will apply.
 *
 * `profile` arms every key-channel binding underneath it; `binding` arms one
 * row. The distinction is not cosmetic — see the warning rendered below — so
 * the host has to state it rather than let the component guess from context.
 */
export type BlacklistArmingScope = 'profile' | 'binding';

export function EgressAdmissionBlacklistFields({
  value,
  scope,
  disabled,
  idPrefix,
  onChange,
}: {
  readonly value: EgressAdmissionBlacklist | null;
  readonly scope: BlacklistArmingScope;
  readonly disabled: boolean;
  readonly idPrefix: string;
  readonly onChange: (next: EgressAdmissionBlacklist) => void;
}): JSX.Element {
  const t = useT();
  const armed = armedBlacklistRules(value);
  return (
    <div
      className="flex flex-col gap-3 rounded-lg bg-card p-3"
      data-testid={`${idPrefix}-egress-blacklist`}
    >
      <h4 className="m-0 text-label font-medium text-paper-mid">
        {t('egress_editor.blacklist')}
      </h4>
      {scope === 'profile' ? (
        // The blast radius, stated where the mistake would be made. A profile
        // is shared: production runs the `claude-code` and `kiro` namespaces
        // off one, and `kiro`'s bare-API traffic is correct by design and
        // succeeds, so arming `bareApiOnCcLane` here refuses working traffic
        // to fix a different namespace's problem.
        <p
          className="m-0 rounded bg-warn-dim/40 p-2 text-label text-warn"
          data-testid={`${idPrefix}-egress-blacklist-profile-scope-warning`}
        >
          {t('egress_editor.blacklist_profile_scope_warning')}
        </p>
      ) : null}
      {armed.length === 0 ? (
        <p
          className="m-0 rounded bg-well p-2 text-label text-paper-mid"
          data-testid={`${idPrefix}-egress-blacklist-inert-note`}
        >
          {t('egress_editor.blacklist_inert_note')}
        </p>
      ) : null}
      {EGRESS_ADMISSION_BLACKLIST_RULES.map((rule) => {
        const configured = value?.[rule];
        return (
          <div key={rule} className="grid gap-3 sm:grid-cols-2">
            <Checkbox
              checked={configured !== undefined}
              onCheckedChange={(checked) => onChange(blacklistWithRule(
                value,
                rule,
                checked ? { action: configured?.action ?? DEFAULT_BLACKLIST_ACTION } : undefined,
              ))}
              disabled={disabled}
              label={t(RULE_LABEL_KEYS[rule])}
              testId={`${idPrefix}-egress-blacklist-${rule}`}
            />
            {configured !== undefined ? (
              <FormField
                label={t('egress_editor.blacklist_action')}
                fieldId={`${idPrefix}-egress-blacklist-${rule}-action`}
                hint={t('egress_editor.blacklist_action_hint')}
              >
                <Select
                  value={configured.action}
                  onValueChange={(action) => {
                    if (isOneOf(action, EGRESS_ADMISSION_BLACKLIST_ACTIONS)) {
                      onChange(blacklistWithRule(value, rule, { action }));
                    }
                  }}
                  disabled={disabled}
                  ariaLabel={`${rule} ${t('egress_editor.blacklist_action')}`}
                  testId={`${idPrefix}-egress-blacklist-${rule}-action`}
                  mono
                  options={EGRESS_ADMISSION_BLACKLIST_ACTIONS.map((action) => ({
                    value: action,
                    label: action,
                  }))}
                />
              </FormField>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
